import { useEffect, useState } from "react";

import { appConfig } from "../config";
import { parseYesPrice, polymarketOddsUrl } from "./polymarket";
import type { PolymarketMarket } from "./polymarket";

export interface PolymarketOddsState {
  odds: Map<string, number>;
  loading: boolean;
}

export function usePolymarketOdds(slugs: string[]): PolymarketOddsState {
  const [odds, setOdds] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(false);
  const key = uniqueSorted(slugs).join("|");

  useEffect(() => {
    const uniqueSlugs = key ? uniqueSorted(slugs) : [];

    if (uniqueSlugs.length === 0) {
      setOdds(new Map());
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);

    const load = async (): Promise<void> => {
      try {
        const response = await fetch(polymarketOddsUrl(uniqueSlugs));
        if (!response.ok) {
          throw new Error(`Polymarket request failed: ${response.status}`);
        }

        const markets = (await response.json()) as PolymarketMarket[];
        if (!active) return;

        const next = new Map<string, number>();
        markets.forEach((market) => {
          const price = parseYesPrice(market);
          if (market.slug && price !== undefined) {
            next.set(market.slug, price);
          }
        });
        setOdds(next);
      } catch {
        // Keep the last good odds; affected rows fall back to "—".
      } finally {
        if (active) setLoading(false);
      }
    };

    void load();
    const timer = setInterval(
      () => void load(),
      appConfig.polymarket.oddsRefreshMs,
    );

    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [key]);

  return { odds, loading };
}

function uniqueSorted(slugs: string[]): string[] {
  return Array.from(
    new Set(slugs.filter((slug) => slug.trim().length > 0)),
  ).sort();
}
